import { AnalysisAndProjectsSharedSearchSchema } from '@/features/search/_models/shared-search';
import {
  fundSheetTypeEnum,
  ksAggregationTargetTypeEnum,
  ksAnalysisAxisEnum,
  sheetTypeEnum,
} from '@/utils/enum';
import type dayjs from 'dayjs';
import { z } from 'zod';

const SharedSearch = AnalysisAndProjectsSharedSearchSchema.shape;

/**
 * 基金シート分析 (KS Analysis) の検索フォームスキーマ。
 * BE: GET /api/.../ks-analysis/ (KsAnalysisRequestParams) に対応する。
 *
 * - fiscalYear は **単一年度** （RS の fiscalYearRange (DateRange) とは異なる）。
 * - sheetType は 'KS' 固定。共通フィールド (`KeywordColField` 等) が
 *   `useWatch('sheetType')` で表示判定するため、フォーム値として保持する。
 */
export const KsAnalysisSearchSchema = z.object({
  sheetType: SharedSearch.sheetType,
  // KS 分析タブは基金シート (standard) 固定。フォーム表示はしないが、
  // 共有 `KeywordColField` の検索対象ヒントが fundSheetType ベースで切り替わるため、
  // 事業一覧の基金（標準）と同じヒントを出すためにフォーム値として保持する。
  fundSheetType: fundSheetTypeEnum,
  fiscalYear: z.custom<dayjs.Dayjs>().nullable(),
  q: SharedSearch.q,
  isSearchTargetProjectName: SharedSearch.isSearchTargetProjectName,
  corporateNumber: SharedSearch.corporateNumber,
  projectNumbers: SharedSearch.projectNumbers,
  // cascader項目: 事業所管課室のみ KS で利用
  nestedOrganizationIds: SharedSearch.nestedOrganizationIds,
  aggregationTargetType: ksAggregationTargetTypeEnum,
  analysisAxis: ksAnalysisAxisEnum.nullable(),
});

export type KsAnalysisSearchFormType = z.infer<typeof KsAnalysisSearchSchema>;

type KsAnalysisSearchKeysType = keyof typeof KsAnalysisSearchSchema.shape;
export const KsAnalysisSearchKeys = Object.keys(
  KsAnalysisSearchSchema.shape,
) as KsAnalysisSearchKeysType[];

/**
 * KS 分析検索フォームの初期値。
 * - fiscalYear は number で保持し、Accordion / SearchButton 側で `dayjs().year()` に変換する。
 *   （nuqs の URL クエリも number で保持するため、初期値は number で持つのが整合が良い）
 * - `analysisAxis` の URL state は `withDefault('organization')`。合計のみ（軸なし）は URL から
 *   消すのではなく `total` センチネルで表現し、初回表示（organization）と区別する（RS-4424）。
 *   フォームでは `total`→null、API では `total` を除外して null を送る。
 *   SearchButton クリア時はこの `initialValue.analysisAxis` で `organization` に戻る。
 */
export const initialValueOfKsAnalysisSearchForm = (currentFiscalYear: number) =>
  ({
    sheetType: sheetTypeEnum.enum.KS,
    fundSheetType: fundSheetTypeEnum.enum.standard,
    fiscalYear: currentFiscalYear,
    aggregationTargetType:
      ksAggregationTargetTypeEnum.enum['fund-balance-at-the-end-of-the-year'],
    analysisAxis: ksAnalysisAxisEnum.enum.organization,
    isSearchTargetProjectName: false,
  }) as const;
